import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useSeo } from '@kilat/ui';

import { usePlatformBrand } from './use-platform-brand.js';
import {
  addLocalePrefix,
  localeFromPathname,
  prefixedLocales,
  stripLocalePrefix,
} from './locale-routing.js';

/**
 * Per-route, per-locale SEO head sync (SEO slice 2b — ADR-0024).
 *
 * Wraps the shared `useSeo` (title + description + og:title/og:description) and
 * additionally manages the two locale-URL tags that slice 2a could not emit,
 * because locale wasn't in the URL:
 *
 *   - `<link rel="canonical">` → the current locale's absolute URL.
 *   - `<link rel="alternate" hreflang="…">` for every supported locale plus
 *     `x-default` → the en (bare) URL.
 *   - `<meta property="og:url">` → the current locale's absolute URL, and
 *     `<meta property="og:locale">` / `og:locale:alternate` → the OpenGraph
 *     locale codes (e.g. `en_MY`, `ms_MY`). These land in the STATIC prerendered
 *     `<head>` (slice 2b part B), not just client-injected.
 *
 * The absolute URLs derive from the resolved brand's marketing domain
 * (`brand.domains.marketing`) and the current route's locale-stripped path, so
 * a multi-brand host emits its own origin. Managed alternate links are tagged
 * `data-seo-alt` and cleared/rebuilt on every route change (this also replaces
 * the build-time home defaults in `index.html`); canonical + og:url/og:locale
 * are restored to their previous values on unmount.
 */

/**
 * OpenGraph locale codes per supported UI locale, mirroring the build-time
 * defaults baked into `index.html`. (`og:locale` wants a `language_TERRITORY`
 * code, not the bare BCP-47 tag.)
 */
const OG_LOCALES: Record<string, string> = {
  en: 'en_MY',
  ms: 'ms_MY',
  zh: 'zh_CN',
  ta: 'ta_IN',
};
export interface UseMarketingSeoOptions {
  readonly title?: string | null;
  readonly description?: string | null;
}

export function useMarketingSeo({
  title = null,
  description = null,
}: UseMarketingSeoOptions): void {
  useSeo({ title, description });

  const brand = usePlatformBrand();
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const origin = `https://${brand.domains.marketing}`;
    const supported = brand.supportedLocales;
    const defaultLocale = brand.defaultLocale;
    const prefixed = prefixedLocales(supported, defaultLocale);
    const basePath = stripLocalePrefix(pathname, prefixed);
    const activeLocale = localeFromPathname(pathname, supported, defaultLocale);
    const urlFor = (loc: (typeof supported)[number]) =>
      origin + addLocalePrefix(basePath, loc, defaultLocale);

    const restores: (() => void)[] = [];
    const head = document.head;

    // Update a single existing `<meta>`'s content in place, restoring on unmount.
    const setMetaContent = (selector: string, value: string) => {
      const el = head.querySelector(selector);
      if (!el) return;
      const previous = el.getAttribute('content');
      el.setAttribute('content', value);
      restores.push(() => {
        if (previous !== null) el.setAttribute('content', previous);
      });
    };

    // canonical → the current locale's URL (update the existing tag in place).
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      const previous = canonical.getAttribute('href');
      canonical.setAttribute('href', urlFor(activeLocale));
      restores.push(() => {
        if (previous !== null) canonical.setAttribute('href', previous);
      });
    }

    // og:url + og:locale → the current locale's URL + OpenGraph locale code.
    setMetaContent('meta[property="og:url"]', urlFor(activeLocale));
    const activeOg = OG_LOCALES[activeLocale];
    if (activeOg) setMetaContent('meta[property="og:locale"]', activeOg);

    // Managed head elements cleared + rebuilt on every route change: hreflang
    // alternates and og:locale:alternate (incl. the index.html build defaults).
    head
      .querySelectorAll('link[rel="alternate"][data-seo-alt], meta[property="og:locale:alternate"]')
      .forEach((el) => el.remove());
    const created: Element[] = [];
    const addAlternate = (hreflang: string, href: string) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', href);
      link.setAttribute('data-seo-alt', '');
      head.appendChild(link);
      created.push(link);
    };
    for (const loc of supported) addAlternate(loc, urlFor(loc));
    addAlternate('x-default', urlFor(defaultLocale));

    // og:locale:alternate → every supported locale except the active one.
    for (const loc of supported) {
      const og = OG_LOCALES[loc];
      if (loc === activeLocale || !og) continue;
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:locale:alternate');
      meta.setAttribute('content', og);
      meta.setAttribute('data-seo-alt', '');
      head.appendChild(meta);
      created.push(meta);
    }

    return () => {
      for (const el of created) el.remove();
      for (const restore of restores) restore();
    };
  }, [brand, pathname]);
}
